import { useEffect, useState } from 'react';
import { showErrorToast, showSuccessToast } from '@app/components/CommonToast';
import { MessageUtils } from '@app/modules/errors';
import { UserUtils } from '@app/modules/userinfo';
import { commonApiService } from '@app/services/commonApi';
import {
  ConfirmedMessageListData,
  NotificationData,
  UnreadMessageData,
  UpdateConfirmStatusPayload,
  UpdateReadStatusData,
  UpdateReadStatusPayload,
} from '@app/types/initScreen';
import { useTranslation } from 'react-i18next';

interface UseNotificationSenderResult {
  loadingConfirmedMessageList?: boolean;
  loadingUnreadMessage?: boolean;
  loadingReadAllMessages?: boolean;
  confirmedMessageListData?: ConfirmedMessageListData[] | null;
  unreadMessageData?: UnreadMessageData | null;
  updateReadStatusData?: UpdateReadStatusData | null;
  showNotificationDialog?: boolean;
  setShowNotificationDialog?: React.Dispatch<React.SetStateAction<boolean>>;
  confirmedMessageListApi?: () => Promise<void>;
  unreadMessageListApi?: () => Promise<void>;
  setLoadingConfirmedMessageList?: React.Dispatch<React.SetStateAction<boolean>>;
  setLoadingUnreadMessage?: React.Dispatch<React.SetStateAction<boolean>>;
  readAllMessagesApi?: () => Promise<void>;
  updateReadStatusApi?: (payload: UpdateReadStatusPayload) => Promise<UpdateReadStatusData | null>;
  updateConfirmStatusApi?: (payload: UpdateConfirmStatusPayload) => Promise<void>;
}

export function useNotificationSender(): UseNotificationSenderResult {
  const { t, i18n } = useTranslation();
  const [loadingConfirmedMessageList, setLoadingConfirmedMessageList] = useState(false);
  const [loadingReadAllMessages, setLoadingReadAllMessages] = useState(false);
  const [confirmedMessageListData, setConfirmedMessageListData] = useState<ConfirmedMessageListData[] | null>(null);
  const [loadingUnreadMessage, setLoadingUnreadMessage] = useState(false);
  const [unreadMessageData, setUnreadMessageData] = useState<UnreadMessageData | null>(null);
  const [updateReadStatusData, setUpdateReadStatusData] = useState<UpdateReadStatusData | null>(null);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);

  /**
   * Sort notifications by priority:
   * 1. Unread messages (readFlg = false) displayed first
   * 2. Read but not confirmed messages (readFlg = true && confirmFlg = false)
   * 3. Within same group, sort by updatedAt descending (newest first)
   */
  const sortNotificationsByPriority = (notifications: NotificationData[]) => {
    return notifications.sort((a, b) => {
      /** Determine priority group for each notification */
      const getPriority = (notification: NotificationData) => {
        if (notification?.readFlg === false) {
          return 1; /** Highest priority: Unread */
        }
        if (notification?.readFlg === true && notification?.confirmFlg === false) {
          return 2; /** Medium priority: Read but not confirmed */
        }
        return 3; /** Lowest priority: Read and confirmed */
      };

      const priorityA = getPriority(a);
      const priorityB = getPriority(b);

      /** Priority 1: Sort by priority group */
      if (priorityA !== priorityB) {
        return priorityA - priorityB; /** Lower number = higher priority */
      }

      /** Priority 2: Within same group, sort by updatedAt descending (newest first) */
      const dateA = new Date(a?.updatedAt ?? '').getTime();
      const dateB = new Date(b?.updatedAt ?? '').getTime();
      return dateB - dateA; /** Descending order (newest first) */
    });
  };

  /** Fetch API-NOTIFY-01 unread message list data */
  const unreadMessageListApi = async () => {
    const userInfo = UserUtils.get();
    if (!userInfo?.user?.userId) return;
    try {
      setLoadingUnreadMessage(true);
      const response = await commonApiService.unreadMessageList();
      if (response?.success && response?.data) {
        /** Sort notifications by priority before setting state */
        const sortedNotifications = sortNotificationsByPriority(response?.data?.content?.notificationList ?? []);
        /** Update state with sorted notifications */
        setUnreadMessageData({
          ...response?.data?.content,
          notificationList: sortedNotifications,
        });
      }
    } catch (error: any) {
      showErrorToast(MessageUtils.formatApiMessage(error) ?? t(error?.code, { ns: 'messages' }));
    } finally {
      setLoadingUnreadMessage(false);
    }
  };

  /** Fetch API-NOTIFY-02 update read status */
  const updateReadStatusApi = async (payload: UpdateReadStatusPayload) => {
    try {
      const response = await commonApiService.updateReadStatus(payload);
      if (response?.success && response?.data) {
        /** Update the state with the latest read status data */
        setUpdateReadStatusData(response?.data?.content ?? null);
        /** Update unread message data with the latest read status */
        setUnreadMessageData((prevData) => {
          /** If no previous data, return as is */
          if (!prevData) return prevData;
          /** Update the specific notification's updatedAt based on API response */
          const updatedNotifications = prevData.notificationList?.map((notification) => {
            /** Match the notification to update based on notificationIconId */
            if (Number(notification?.notificationIconId) === Number(response?.data?.content?.notificationIconId)) {
              /** Update the notification's updatedAt based on API response, keep other properties unchanged */
              return {
                ...notification,
                readFlg: true /** Mark as read */,
                updatedAt:
                  response?.data?.content?.updatedAt ??
                  notification.updatedAt /** Update updatedAt based on API response */,
              };
            }
            /** For other notifications, return as is */
            return notification;
          });
          return {
            ...prevData,
            notificationList: updatedNotifications,
          };
        });
        /** Return the response data so it can be used immediately */
        return response?.data?.content ?? null;
      }
      return null;
    } catch (error: any) {
      showErrorToast(MessageUtils.formatApiMessage(error) ?? t(error?.code, { ns: 'messages' }));
      return null;
    }
  };

  /** Fetch API-NOTIFY-06 update confirm status */
  const updateConfirmStatusApi = async (payload: UpdateConfirmStatusPayload) => {
    try {
      const response = await commonApiService.updateConfirmStatus(payload);
      if (response?.success) {
        /** Display the success toast message (if any). */
        showSuccessToast(t('MSG_INFO_COM_00101', { ns: 'messages', 0: t('common.notificationMessage') }));
        /** Update unread message data with the latest confirm status */
        setUnreadMessageData((prevData) => {
          /** If no previous data, return as is */
          if (!prevData) return prevData;
          /** Update the specific notification's confirmFlg based on API response */
          const updatedNotifications = prevData.notificationList?.map((notification) => {
            /** Match the notification to update based on notificationIconId */
            if (Number(notification?.notificationIconId) === Number(payload?.notificationIconId)) {
              /** Update the notification's confirmFlg based on API response, keep other properties unchanged */
              return {
                ...notification,
                readFlg: true /** Mark as read (if not already) disable the Confirm button (set it to Inactive). */,
                confirmFlg: true /** Update confirmFlg = true in the local state. */,
              };
            }
            return notification;
          });
          /** Update the message list on the CM006 screen. */
          const sortedNotifications = sortNotificationsByPriority(updatedNotifications ?? []);
          return {
            ...prevData,
            notificationList: sortedNotifications,
          };
        });
        /** Close the CM011 dialog. */
        setShowNotificationDialog(false);
      }
    } catch (error: any) {
      showErrorToast(MessageUtils.formatApiMessage(error) ?? t(error?.code, { ns: 'messages' }));
      setUnreadMessageData((prevData) => {
        if (!prevData) return prevData;
        const updatedNotifications = prevData.notificationList?.map((notification) => {
          if (Number(notification?.notificationIconId) === Number(payload?.notificationIconId)) {
            return {
              ...notification,
              readFlg: false /** Switch the Confirm button to the Active state. */,
            };
          }
          return notification;
        });
        return {
          ...prevData,
          notificationList: updatedNotifications,
        };
      });
    }
  };

  /** Fetch API-NOTIFY-03 confirmed message list data */
  const confirmedMessageListApi = async () => {
    const userInfo = UserUtils.get();
    if (!userInfo?.user?.userId) return;
    try {
      setLoadingConfirmedMessageList(true);
      const response = await commonApiService.confirmedMessageList();
      if (response?.success && response?.data) {
        setConfirmedMessageListData(response?.data?.content ?? null);
      }
    } catch (error: any) {
      showErrorToast(MessageUtils.formatApiMessage(error) ?? t(error?.code, { ns: 'messages' }));
    } finally {
      setLoadingConfirmedMessageList(false);
    }
  };

  /** Fetch API-NOTIFY-04 read all messages data */
  const readAllMessagesApi = async () => {
    const userInfo = UserUtils.get();
    if (!userInfo?.user?.userId) return;
    try {
      setLoadingReadAllMessages(true);
      const response = await commonApiService.readAllMessages();
      if (response.success) {
        /** Toast message displayed successfully: MSG_INFO_COM_00061 ("Notifications have been updated as usual"). */
        showSuccessToast(t('MSG_INFO_COM_00061', { ns: 'messages', 0: t('common.notificationMessage') }));
        /** Call API-NOTIFY-01 again to refresh the notification list and update the badge. */
        await unreadMessageListApi();
        /** UI update: all notifications now show as read (default background color). */
        setUnreadMessageData((prevData) => {
          /** If no previous data, return as is */
          if (!prevData) return prevData;
          /** Update all notifications to readFlg: true, but keep other properties unchanged */
          const updatedNotifications = prevData.notificationList?.map((notification) => ({
            ...notification,
            readFlg: true /** Set all notifications to read */,
          }));
          /** Sort the updated notifications by priority before setting state */
          const sortedNotifications = sortNotificationsByPriority(updatedNotifications ?? []);
          return {
            ...prevData,
            notificationList: sortedNotifications,
          };
        });
        /** Off loading state */
        setLoadingReadAllMessages(false);
      }
    } catch (error: any) {
      showErrorToast(MessageUtils.formatApiMessage(error) ?? t(error?.code, { ns: 'messages' }));
    }
  };

  /** Effect to fetch confirmed message list when loading state is true or language changes */
  useEffect(() => {
    if (!loadingConfirmedMessageList) {
      return;
    }
    confirmedMessageListApi();
  }, [i18n.language, loadingConfirmedMessageList]);

  /** Effect to fetch unread message list when loading state is true or language changes */
  useEffect(() => {
    if (!loadingUnreadMessage) {
      return;
    }
    unreadMessageListApi();
  }, [i18n.language, loadingUnreadMessage]);

  return {
    showNotificationDialog,
    loadingUnreadMessage,
    loadingReadAllMessages,
    unreadMessageData,
    updateReadStatusData,
    loadingConfirmedMessageList,
    confirmedMessageListData,
    confirmedMessageListApi,
    setLoadingConfirmedMessageList,
    unreadMessageListApi,
    setLoadingUnreadMessage,
    readAllMessagesApi,
    updateReadStatusApi,
    updateConfirmStatusApi,
    setShowNotificationDialog,
  };
}
